import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Platform,
  StyleSheet,
  TextInput,
  TouchableOpacity,
} from 'react-native';

import { GetDays, MONTH_NAME } from '@components/DatePicker/DatePicker';
import {
  isEndpointDayStyle,
  RangeDayCell,
} from '@components/DatePicker/HoverableDayCell';
import { beginningOfToday } from '@utils/formatters/date';

import theme from '../../../themes/theme';
import { Box, Text } from '../Restyle';
import Icon from '../shared/Icon/Icon';
import { WebIcon } from '../shared/Icon/WebIcon';
import { Spinner } from '../shared/Spinner';

type DateRangePicker3Props = {
  start?: Date;
  end?: Date;
  confirmButtonText?: string;
  onSelect: (start: Date | undefined, end: Date | undefined) => void;
  onClose: () => void;
  isLoading?: boolean;
};

type DayCellProps = {
  day: number;
  dayStyle: object;
  position: number;
  totalCells: number;
  showRangeCapStart: boolean;
  showRangeCapEnd: boolean;
  onPress: () => void;
};

// TA-10950 #7/#26: grey01 hover on every selectable date.
const DayCell: React.FC<DayCellProps> = ({
  day,
  dayStyle,
  position: i,
  totalCells,
  showRangeCapStart,
  showRangeCapEnd,
  onPress,
}) => {
  const isEndpoint = isEndpointDayStyle(dayStyle, styles);
  const isRowEnd = i % 7 == 0;

  let rangeCapEndStyle;
  if (showRangeCapEnd) {
    rangeCapEndStyle =
      i == totalCells
        ? styles.selectedDayContainer2RowEnd
        : styles.selectedDayContainer2;
  }

  let selectedDotStyle;
  if (dayStyle === styles.daySelect || dayStyle === styles.dayToday) {
    selectedDotStyle = {
      container: styles.selectedDayContainer,
      dot: dayStyle === styles.daySelect ? styles.selectedDay : styles.dayToday,
    };
  }

  let startCapStyle;
  if (
    dayStyle === styles.daySelectStart ||
    dayStyle === styles.todaySelectStart
  ) {
    startCapStyle = {
      container: isRowEnd
        ? styles.selectedDayContainerStartWhenInRowEnd
        : styles.selectedDayContainerStart,
      dot: styles.selectedDayStart,
    };
  }

  let endCapStyle;
  if (dayStyle === styles.daySelectEnd || dayStyle === styles.todaySelectEnd) {
    endCapStyle = {
      container: styles.selectedDayContainerEndWhenInRowStart,
      dot: styles.selectedDayEndWhenInRowStart,
    };
  }

  let inSelectionStyle;
  if (
    dayStyle === styles.dayInSelection ||
    dayStyle === styles.todayInSelection
  ) {
    inSelectionStyle = isRowEnd
      ? styles.selectedDayContainer2RowEnd
      : styles.selectedDayContainer2;
  }

  return (
    <RangeDayCell
      day={day}
      dayStyle={dayStyle}
      dayViewItemStyle={styles.dayViewItem}
      textVariant='webBodySecondary'
      hoverDisabled={day === 0 || isEndpoint}
      onPress={onPress}
      cursor={day === 0 ? 'default' : 'pointer'}
      // N2 review fix, round 3 correction: the cell's own touch/layout
      // footprint stays 48 wide to match the weekday header and the
      // 48/52-wide range bars (shrinking it clipped both). Only the hover
      // tint itself is a centered 40x40 circle, matching dayViewItem/daySelect's
      // own circle - radius=20 on the full 48-wide box rendered a pill.
      radius={20}
      width={48}
      height={40}
      hoverWidth={40}
      hoverHeight={40}
      innerBoxSize={{ width: 48, height: 40 }}
      marginHorizontal={Platform.OS !== 'web' ? 'xxs' : 'none'}
      rangeCapStartStyle={
        showRangeCapStart ? styles.selectedDayContainer2 : undefined
      }
      rangeCapEndStyle={rangeCapEndStyle}
      selectedDotStyle={selectedDotStyle}
      startCapStyle={startCapStyle}
      endCapStyle={endCapStyle}
      inSelectionStyle={inSelectionStyle}
    />
  );
};

// DateRangePicker3 is adujsted to global activity timesheet modal web
export const DateRangePicker3: React.FC<DateRangePicker3Props> = ({
  start,
  end,
  onSelect,
  confirmButtonText,
  onClose = () => {},
  isLoading = false,
}) => {
  const date = beginningOfToday();
  const { t } = useTranslation('format');
  const xYear = date.getFullYear();
  const xMonth = date.getMonth();

  const [year, setYear] = useState(xMonth === 0 ? xYear - 1 : xYear);
  const [month, setMonth] = useState(xMonth === 0 ? 11 : xMonth - 1);

  const [weeks, setWeeks] = useState<any[]>([]);
  const [nextWeeks, setNextWeeks] = useState<any[]>([]);
  const [currentMonthDay, setCurrentMonthDay] = useState<any[]>([]);
  const [nextMonthDay, setNextMonthDay] = useState<any[]>([]);

  const [startDate, setStartDate] = useState(start);
  const [untilDate, setUntilDate] = useState(end);

  useEffect(() => {
    const _weeks = getWeek(currentMonthDay, false);
    setWeeks(_weeks);

    const _nextWeeks = getWeek(nextMonthDay, true);

    setNextWeeks(_nextWeeks);
  }, [startDate, untilDate]);

  useEffect(() => {
    GetMonthData(year, month);
  }, [year, month]);

  const GetMonthDataStep1 = (year: number, month: number) => {
    const currentMonthDays = [];

    const days = GetDays(year, month);

    const firstDay = new Date(year, month, 1);

    const week = firstDay.getDay();

    for (let i = 0; i < week; i++) {
      currentMonthDays.push(0);
    }

    for (let i = 1; i <= days; i++) {
      currentMonthDays.push(i);
    }

    const offset = currentMonthDays.length % 7;

    if (offset != 0) {
      for (let i = 0; i < 7 - offset; i++) {
        currentMonthDays.push(0);
      }
    }
    return currentMonthDays;
  };
  const GetMonthData = (year: number, month: number) => {
    const currentMonthDays = GetMonthDataStep1(year, month);

    setCurrentMonthDay(currentMonthDays);
    const _weeks = getWeek(currentMonthDays, false);
    setWeeks(_weeks);
    const nextCurrentMonthDays =
      month === 11
        ? GetMonthDataStep1(year + 1, 0)
        : GetMonthDataStep1(year, month + 1);
    setNextMonthDay(nextCurrentMonthDays);
    setNextWeeks(getWeek(nextCurrentMonthDays, true));
  };

  const onSelectDay = (dateSelected: Date) => {
    if (!startDate) {
      setStartDate(dateSelected);
    } else if (!untilDate) {
      if (dateSelected >= startDate) {
        setUntilDate(dateSelected);
      } else {
        setStartDate(dateSelected);
      }
    } else {
      setStartDate(dateSelected);
      setUntilDate(undefined);
    }
  };

  const monthPre = () => {
    if (month == 0) {
      setMonth(11);
      setYear(year - 1);
    } else {
      setMonth(month - 1);
    }
  };

  const monthNext = () => {
    if (month == 11) {
      setMonth(0);
      setYear(year + 1);
    } else {
      setMonth(month + 1);
    }
  };

  const setDayStyle = (xyear: number, xmonth: number, xday: number) => {
    if (xday === 0) return {};

    const thisDate = new Date(xyear, xmonth, xday);
    const formattedDate = dayjs(thisDate).format('DD/MM/YYYY');
    const currentFormattedDate = dayjs().format('DD/MM/YYYY');
    const startFormattedDate = startDate
      ? dayjs(startDate).format('DD/MM/YYYY')
      : null;
    const untilFormattedDate = untilDate
      ? dayjs(untilDate).format('DD/MM/YYYY')
      : null;

    if (
      startDate &&
      untilDate &&
      startFormattedDate === untilFormattedDate &&
      formattedDate === startFormattedDate
    ) {
      return styles.daySelect;
    }
    if (formattedDate === currentFormattedDate) {
      if (
        startDate &&
        untilDate &&
        startDate < thisDate &&
        untilDate > thisDate &&
        xday > 0
      ) {
        return styles.todayInSelection;
      } else if (
        (startDate &&
          untilDate &&
          startFormattedDate !== untilFormattedDate &&
          thisDate === untilDate) ||
        formattedDate === untilFormattedDate
      ) {
        return styles.todaySelectEnd;
      } else if (
        (startDate &&
          untilDate &&
          startFormattedDate !== untilFormattedDate &&
          thisDate === untilDate) ||
        formattedDate === startFormattedDate
      ) {
        return styles.todaySelectStart;
      } else {
        return styles.dayToday;
      }
    }
    if (formattedDate === startFormattedDate) {
      return styles.daySelectStart;
    }
    if (formattedDate === untilFormattedDate) {
      return styles.daySelectEnd;
    }
    if (
      startDate &&
      untilDate &&
      startDate < thisDate &&
      untilDate > thisDate &&
      xday > 0
    ) {
      return styles.dayInSelection;
    }
    if (xday < 1) {
      return styles.dayViewItem;
    }

    return {};
  };

  const weekDayComponent = () => (
    <Box
      flexDirection='row'
      style={{
        marginBottom: 13,
      }}
      px='m'
      py='xs'>
      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((i) => {
        counter++;
        return (
          <Box width={48} height={16} key={counter}>
            <Text
              key={counter}
              variant='webBodySecondary'
              color='black'
              textAlign='center'>
              {i}
            </Text>
          </Box>
        );
      })}
    </Box>
  );
  const getWeek = (days: number[], isNext: boolean) => {
    let xyear = year;
    let xmonth = month;

    if (isNext) {
      xyear = month === 11 ? year + 1 : year;
      xmonth = month === 11 ? 0 : month + 1;
    }

    const theFirstDay = new Date(xyear, xmonth, 1);
    const theLastDay = new Date(xyear, xmonth, GetDays(xyear, xmonth));

    const _weeks = [];
    let week = [];

    for (let i = 1; i <= days.length; i++) {
      const dayStyle = setDayStyle(xyear, xmonth, days[i - 1]);
      const showRangeCapStart =
        days[i - 1] == 0 &&
        i < 7 &&
        !!startDate &&
        !!untilDate &&
        theFirstDay > startDate &&
        theFirstDay <= untilDate;
      const showRangeCapEnd =
        days[i - 1] == 0 &&
        i > 7 &&
        !!startDate &&
        !!untilDate &&
        theLastDay >= startDate &&
        theLastDay < untilDate;
      week.push(
        <DayCell
          key={xyear + xmonth + 'touch' + i}
          day={days[i - 1]}
          dayStyle={dayStyle}
          position={i}
          totalCells={days.length}
          showRangeCapStart={showRangeCapStart}
          showRangeCapEnd={showRangeCapEnd}
          onPress={() => {
            const day = days[i - 1];
            if (day === 0) return;
            onSelectDay(new Date(xyear, xmonth, day));
          }}
        />
      );

      if (i % 7 == 0) {
        _weeks.push(
          <Box
            key={xyear + xmonth + 'week' + i}
            flexDirection='row'
            style={{
              marginBottom: 8,
            }}
            px='m'>
            {week}
          </Box>
        );
        week = [];
      }
    }
    return _weeks;
  };

  let counter = 0;

  return (
    <Box flex={Platform.OS === 'web' ? 1 : 0.7}>
      <Box
        height={72}
        my='m'
        justifyContent='flex-start'
        flexDirection='row'
        alignItems='flex-start'>
        <Box flex={1} mr='m'>
          <Text variant='labelSmall' color='textPrimary' numberOfLines={1}>
            {t('shared:type')}
          </Text>

          <Box
            mt='xs'
            mr='xs'
            height={48}
            width={134}
            flexDirection='row'
            borderWidth={1}
            borderColor='grey02'
            borderRadius='xs'
            alignItems='center'
            justifyContent='center'>
            <Text variant='webBodySecondary' color='textPrimary'>
              {t('models:activity.timeSheet')}
            </Text>
            <Icon variant='l' name='ChevronDown' color='textPrimary' />
          </Box>
        </Box>
        <Box flex={1} mr='m' />
        <Box flex={1} mr='m'>
          <Text variant='labelSmall' color='textPrimary' numberOfLines={1}>
            {t('models:projects.taskList.dateRangeStart')}
          </Text>
          <Box
            height={48}
            mt='xs'
            px='xxs'
            backgroundColor='white'
            borderColor='grey02'
            borderWidth={1}
            borderRadius='xs'
            flexDirection='row'
            alignItems='center'
            accessibilityLabel={t('models:projects.taskList.dateRangeStart')}>
            <TextInput
              editable={false}
              style={[
                styles.input,
                theme.textVariants.webBodySecondary,
                { height: 40 },
              ]}
              value={startDate ? dayjs(startDate).format('MM / DD / YYYY') : ''}
              autoCapitalize='none'
            />
          </Box>
        </Box>
        <Box flex={1}>
          <Text variant='labelSmall' color='textPrimary' numberOfLines={1}>
            {t('models:projects.taskList.dateRangeEnd')}
          </Text>
          <Box
            mt='xs'
            backgroundColor='white'
            borderColor='grey02'
            height={48}
            borderWidth={1}
            borderRadius='xs'
            flexDirection='row'
            alignItems='center'
            accessibilityLabel={t('models:projects.taskList.dateRangeEnd')}>
            <TextInput
              editable={false}
              style={[
                styles.input,
                theme.textVariants.webBodySecondary,
                { height: 40 },
              ]}
              value={untilDate ? dayjs(untilDate).format('MM / DD / YYYY') : ''}
              autoCapitalize='none'
            />
          </Box>
        </Box>
      </Box>
      <Box
        flexDirection='row'
        flexWrap='wrap'
        justifyContent='space-between'
        marginBottom='s'
        borderTopWidth={1}
        borderTopColor='grey02'
        alignItems='flex-start'>
        <Box alignItems='center' flexDirection='row' flex={1} mt='m' ml='m'>
          <WebIcon
            onPress={monthPre}
            name='ChevronLeft'
            variant='l'
            color='grey04'
            accessibilityLabel='Previous month'
          />

          <Box alignItems='center' flex={1}>
            <Text textAlign='center' variant='bodyTaskName'>
              {MONTH_NAME[month]} {year}
            </Text>
          </Box>
        </Box>
        <Box alignItems='center' flexDirection='row' flex={1} mt='m' mr='m'>
          <Box alignItems='center' flex={1}>
            <Text textAlign='center' variant='bodyTaskName'>
              {MONTH_NAME[month === 11 ? 0 : month + 1]}{' '}
              {month === 11 ? year + 1 : year}
            </Text>
          </Box>

          <WebIcon
            onPress={monthNext}
            name='ChevronRight'
            variant='l'
            color='grey04'
            accessibilityLabel='Next month'
          />
        </Box>
      </Box>
      <Box flexDirection='row'>
        <Box flex={1} key='week1'>
          {weekDayComponent()}
          {weeks}
        </Box>

        <Box flex={1} key='weeknext'>
          {weekDayComponent()}
          {nextWeeks}
        </Box>
      </Box>

      <Box flex={1}></Box>
      <Box
        flexDirection='row'
        justifyContent='flex-end'
        alignItems='center'
        mt='m'
        left={0}
        right={0}
        marginHorizontal='m'>
        <Box flex={1}></Box>

        <TouchableOpacity
          accessibilityLabel={t('shared:cancel')}
          disabled={isLoading}
          onPress={() => {
            setStartDate(undefined);
            setUntilDate(undefined);
            onClose();
          }}>
          <Box
            height={48}
            width={125}
            flexDirection='row'
            borderColor='black'
            borderWidth={1}
            px='m'
            py='xs'
            backgroundColor='white'
            borderRadius='xs'
            alignItems='center'
            justifyContent='center'
            opacity={isLoading ? 0.5 : 1}>
            <Box
              alignItems='center'
              justifyContent='center'
              style={{ flex: 1 }}>
              <Text variant='labelSmall' color='black'>
                {t('shared:cancel')}
              </Text>
            </Box>
          </Box>
        </TouchableOpacity>
        <Box ml='xs' />
        <TouchableOpacity
          accessibilityLabel={t('models:activity.export')}
          disabled={isLoading}
          onPress={() => {
            onSelect(startDate, untilDate);
          }}>
          <Box
            height={48}
            width={107}
            flexDirection='row'
            borderWidth={1}
            px='m'
            py='xs'
            backgroundColor='black'
            borderRadius='xs'
            alignItems='center'
            justifyContent='center'>
            <Box
              position='relative'
              alignItems='center'
              justifyContent='center'
              style={{ flex: 1 }}>
              {isLoading && (
                <Box
                  position='absolute'
                  top={0}
                  bottom={0}
                  left={0}
                  right={0}
                  alignItems='center'
                  justifyContent='center'>
                  <Spinner
                    size={16}
                    strokeWidth={2}
                    color={theme.colors.white}
                  />
                </Box>
              )}
              <Text
                variant='labelSmall'
                color='white'
                style={{ opacity: isLoading ? 0 : 1 }}>
                {confirmButtonText ?? t('models:activity.export')}
              </Text>
            </Box>
          </Box>
        </TouchableOpacity>
      </Box>
    </Box>
  );
};

const styles = StyleSheet.create({
  input: {
    marginLeft: theme.spacing.xs,
    flex: 1,
    ...Platform.select({ web: { outlineStyle: 'none' } }),
  },
  dayViewItem: {
    width: 40,
    height: 40,
    alignItems: 'center',
    alignContent: 'center',
    textAlign: 'center',
    paddingVertical: 4,
    paddingHorizontal: 4,
  },
  daySelect: {
    color: theme.colors.white,
  },
  todaySelectStart: {
    width: 40,
    height: 40,
    alignSelf: 'center',
    borderRadius: 20,
    borderColor: theme.colors.grey05,
    borderWidth: 1,
    color: theme.colors.grey02,
    zIndex: 100,
  },
  daySelectStart: {
    color: theme.colors.grey02,
    zIndex: 100,
  },
  todaySelectEnd: {
    width: 40,
    height: 40,
    alignSelf: 'center',
    borderRadius: 20,
    borderColor: theme.colors.grey05,
    borderWidth: 1,
    color: theme.colors.white,
    zIndex: 101,
  },
  daySelectEnd: {
    color: theme.colors.white,
    zIndex: 101,
  },
  dayInSelection: {
    // backgroundColor: theme.colors.alertRed,
    // width: 40,
    // height: 40,
    // alignSelf: 'center',
    color: theme.colors.textPrimary,
  },
  todayInSelection: {
    width: 40,
    height: 40,
    alignSelf: 'center',
    borderRadius: 20,
    borderColor: theme.colors.grey05,
    borderWidth: 1,
    color: theme.colors.textPrimary,
  },
  dayNotSelect: {
    color: theme.colors.grey04,
  },
  dayContainer: {
    shadowColor: 'black',
    marginHorizontal: Platform.OS === 'ios' ? -4 : 0,
    shadowOpacity: 0.06,
    shadowOffset: { width: 0, height: 12 },
    shadowRadius: 12,
    backgroundColor: 'white',
  },
  selectedDayContainer: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    right: 0,
    backgroundColor: 'white',
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: -1,
  },
  selectedDayContainerStart: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 24,
    backgroundColor: theme.colors.grey02,
    left: 24,
    zIndex: -1,
  },
  selectedDayContainerStartWhenInRowEnd: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 24,
    backgroundColor: theme.colors.grey02,
    left: 24,
    zIndex: -1,
  },
  selectedDayContainerEndWhenInRowStart: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 30,
    backgroundColor: theme.colors.grey02,
    left: 0,
    zIndex: -1,
  },
  selectedDayContainer2: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    width: 48,
    backgroundColor: theme.colors.grey02,
    // left: -8,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: -10,
  },
  selectedDayContainer2RowEnd: {
    position: 'absolute',
    top: 0,
    left: -4,
    width: 52,
    bottom: 0,
    backgroundColor: theme.colors.grey02,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: -1,
  },
  selectedDayContainer2RowStart: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    backgroundColor: theme.colors.grey02,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: -1,
  },
  dayToday: {
    backgroundColor: theme.colors.white,
    width: 40,
    height: 40,
    alignSelf: 'center',
    borderRadius: 20,
    borderColor: theme.colors.grey05,
    borderWidth: 1,
  },
  selectedDay: {
    backgroundColor: theme.colors.black,
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  selectedDayStart: {
    position: 'absolute',
    top: 0,
    left: -20,
    backgroundColor: theme.colors.black,
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  selectedDayEnd: {
    position: 'absolute',
    top: 0,
    left: 10,
    backgroundColor: theme.colors.black,
    width: 40,
    height: 40,
    borderRadius: 20,
  },
  selectedDayEndWhenInRowStart: {
    position: 'absolute',
    top: 0,
    left: 4,
    backgroundColor: theme.colors.black,
    width: 40,
    height: 40,
    borderRadius: 20,
  },
});
